import { Container, Graphics, Point, Rectangle, Sprite, utils } from 'pixi.js'
import { gsap } from 'gsap'
import ItemBullet from './ItemBullet'
import { dimensions, style, debugMode } from '@/scripts/config/settings'
import { IItem } from '@/scripts/data/types'
import type { Activity } from '@/scripts/data/types'
import { animation } from '@/scripts/config/settings'
import { GlobalState } from '@/scripts/config/globalState'

export default class Item extends Container {
  public readonly id: string
  public readonly activity: Activity
  public readonly itemBullet: ItemBullet

  private readonly center: Point
  private readonly beak?: Point
  private readonly hitRect: Rectangle

  private normalSprite?: Sprite
  private hoverSprite: Sprite
  private isHovered = false

  constructor(positionInfo: IItem, activity: Activity) {
    super()
    this.id = positionInfo.id
    this.activity = activity

    // Bullet
    this.itemBullet = new ItemBullet(
      new Point(positionInfo.hint.x, positionInfo.hint.y),
      activity.title.replace(/\n/g, ' '),
      activity.day,
      dimensions.SCENE_WIDTH,
      style.item.bullet,
    )

    // Position & hit area
    this.position.set(Math.round(positionInfo.x), Math.round(positionInfo.y))
    this.hitRect = new Rectangle(
      positionInfo.hit.x - positionInfo.x,
      positionInfo.hit.y - positionInfo.y,
      positionInfo.hit.w,
      positionInfo.hit.h,
    )
    this.hitArea = this.hitRect
    this.center = new Point(positionInfo.hit.x + positionInfo.hit.w / 2, positionInfo.hit.y + positionInfo.hit.h / 2)
    if (positionInfo.beak) {
      this.beak = new Point(positionInfo.beak.x, positionInfo.beak.y)
    }

    // Sprites
    this.hoverSprite = Sprite.from(`${this.id}_2.png`)
    this.hoverSprite.visible = false
    this.addChild(this.hoverSprite)

    const { numFrames } = GlobalState.itemsConfig[this.activity.positionId]
    if (numFrames > 1) {
      this.normalSprite = Sprite.from(`${this.id}_1.png`)
      this.addChild(this.normalSprite)
    }

    // Interaction setup
    this.visible = false
    this.alpha = 0
    this.eventMode = 'passive'
    this.zIndex = 3

    if (debugMode) this.drawDebugHitArea()
  }

  public show(visible: boolean, instant = false): void {
    if (this.visible === visible) return

    this.eventMode = visible ? 'static' : 'passive'
    gsap.killTweensOf(this)

    if (visible) this.visible = true

    gsap.to(this, {
      duration: instant ? 0 : animation.SHOW_TWEEN_DURATION,
      alpha: visible ? 1 : 0,
      onComplete: () => {
        this.visible = visible
      },
    })
  }

  public setHoverState(hover: boolean): void {
    const canvasContainer = document.querySelector('.canvas-container')
    const cursorClass = this.id === 'bratrstvo' ? 'cursor-paw' : 'cursor-hand'

    if (canvasContainer) {
      if (hover) {
        canvasContainer.classList.add(cursorClass)
      } else {
        canvasContainer.classList.remove(cursorClass)
      }
    }

    if (hover === this.isHovered) return
    this.isHovered = hover

    this.hoverSprite.visible = hover
    if (this.normalSprite) this.normalSprite.visible = !hover
  }

  public getCenterPosition(): Point {
    return this.center
  }

  public getBeakPosition(): Point {
    return this.beak ? this.beak.clone() : this.getCenterPosition()
  }

  private drawDebugHitArea(): void {
    const g = new Graphics()
    g.beginFill(utils.string2hex('0xFFFF00'), 0.4)
    g.drawRect(this.hitRect.x, this.hitRect.y, this.hitRect.width, this.hitRect.height)
    g.endFill()

    // top-left marker
    g.beginFill(utils.string2hex('0xFF0000'), 0.4)
    g.drawCircle(this.hitRect.x, this.hitRect.y, 5)
    g.endFill()

    // center marker
    g.beginFill(utils.string2hex('0xFF0000'), 0.4)
    g.drawCircle(this.center.x - this.x, this.center.y - this.y, 5)
    g.endFill()

    this.addChild(g)
  }
}
